// src/lib/placeholders.ts
// Deterministic placeholder resolver. Proposal sections are riddled with bracket
// placeholders ([COMPANY NAME], [UEI], [POC NAME], [DATE], [SOLICITATION NUMBER]…)
// that the generators emit when a value isn't inlined. These are the bulk of the
// recurring "Unresolved placeholder(s)" critical gaps — and they're fillable
// instantly from the company profile + contract, with NO LLM call. This scans every
// section, fills what it can, and reports what genuinely needs a human.

export interface PlaceholderFill {
  section: string;   // section label (e.g. "Cover Letter", "Technical Approach")
  token: string;     // the literal placeholder, e.g. "[COMPANY NAME]"
  value: string;     // what it will be replaced with
}
export interface PlaceholderGap {
  section: string;
  token: string;     // a placeholder we can't fill from known data
}
export interface PlaceholderScan {
  updatedContentJson: any;            // content_json with fillable placeholders replaced
  filled: PlaceholderFill[];
  unresolved: PlaceholderGap[];
  affectedSections: string[];         // sections that had ≥1 fillable placeholder
}

// Format a business_address jsonb blob into one line.
function formatAddress(addr: any): string {
  if (!addr) return '';
  if (typeof addr === 'string') return addr.trim();
  const line1 = addr.street || addr.address1 || addr.line1 || addr.address || '';
  const city = addr.city || '';
  const state = addr.state || addr.region || '';
  const zip = addr.zip || addr.zipCode || addr.postalCode || addr.postal_code || '';
  const cityState = [city, state].filter(Boolean).join(', ');
  return [line1, [cityState, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ').trim();
}

// Build a resolver: given a placeholder's inner text (lowercased), return a fill or null.
export function buildPlaceholderResolver(profile: any, contract: any, todayISO?: string): (inner: string) => string | null {
  const company = (profile?.company_name || '').trim();
  const uei = (profile?.uei_number || profile?.uei || profile?.duns_number || '').trim();
  const cage = (profile?.cage_code || profile?.cage || '').trim();
  const duns = (profile?.duns_number || '').trim();
  const email = (profile?.email || profile?.contact_email || '').trim();
  const phone = (profile?.phone || profile?.contact_phone || '').trim();
  const website = (profile?.website_url || profile?.website || '').trim();
  const address = formatAddress(profile?.business_address);
  const solicitation = (contract?.solicitationNumber || contract?.solicitation_number ||
    contract?.noticeId || contract?.notice_id || '').trim();
  const naics = (contract?.naicsCode || contract?.naics_code || '').trim();
  const agency = (contract?.agency || '').trim();
  const date = (() => {
    const iso = todayISO || new Date().toISOString();
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso.slice(0, 10)
      : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  })();

  return (innerRaw: string): string | null => {
    // Normalize: strip leading verbs and punctuation ("INSERT ", "ENTER ", "YOUR ").
    const inner = innerRaw.toLowerCase()
      .replace(/^\s*(insert|enter|add|provide|your|the)\s+/g, '')
      .replace(/[.:]+$/, '')
      .trim();
    const has = (...keys: string[]) => keys.some((k) => inner.includes(k));

    if (has('company name', 'offeror name', 'contractor name', 'firm name', 'legal name')) return company || null;
    if (inner === 'company' || inner === 'offeror' || inner === 'contractor') return company || null;
    if (has('uei', 'unique entity')) return uei || null;
    if (has('cage')) return cage || null;
    if (has('duns')) return duns || null;
    if (has('company address', 'offeror address', 'business address', 'mailing address')) return address || null;
    if (inner === 'address') return address || null;
    if (has('email')) return email || null;
    if (has('phone', 'telephone')) return phone || null;
    if (has('website', 'url')) return website || null;
    if (has('solicitation number', 'solicitation #', 'solicitation no', 'rfq number', 'rfp number', 'notice id', 'rfq #', 'rfp #')) return solicitation || null;
    if (inner === 'solicitation' || inner === 'rfq' || inner === 'rfp') return solicitation || null;
    if (has('naics')) return naics || null;
    if (has('agency', 'contracting agency')) return agency || null;
    if (has('date')) return date || null;
    return null;
  };
}

const PLACEHOLDER_RE = /\[([^\]\n]{1,80})\]/g;

// Walk a parsed-JSON value, replacing placeholders in every string leaf.
function walkReplace(
  value: any,
  resolve: (inner: string) => string | null,
  section: string,
  filled: PlaceholderFill[],
  unresolved: PlaceholderGap[],
): any {
  if (typeof value === 'string') {
    return value.replace(PLACEHOLDER_RE, (whole, inner) => {
      // Skip our own citation markers like [PP-001], [CERT-002].
      if (/^[A-Z]{2,5}-\d+$/.test(inner.trim())) return whole;
      const v = resolve(inner);
      if (v && v.trim()) {
        filled.push({ section, token: whole, value: v });
        return v;
      }
      unresolved.push({ section, token: whole });
      return whole;
    });
  }
  if (Array.isArray(value)) return value.map((v) => walkReplace(v, resolve, section, filled, unresolved));
  if (value && typeof value === 'object') {
    const out: any = {};
    for (const k of Object.keys(value)) out[k] = walkReplace(value[k], resolve, section, filled, unresolved);
    return out;
  }
  return value;
}

// Replace placeholders in one section's stored content (JSON string or plain text).
function replaceInSection(
  content: string,
  resolve: (inner: string) => string | null,
  section: string,
  filled: PlaceholderFill[],
  unresolved: PlaceholderGap[],
): string {
  if (!content) return content;
  let parsed: any = null;
  try { parsed = JSON.parse(content); } catch { parsed = null; }
  if (parsed && typeof parsed === 'object') {
    const next = walkReplace(parsed, resolve, section, filled, unresolved);
    return JSON.stringify(next);
  }
  // Plain text section.
  return walkReplace(content, resolve, section, filled, unresolved);
}

// Scan + fill placeholders across the proposal. Returns the would-be-updated
// content_json plus the lists of filled vs. unresolved placeholders. Pure — the
// caller decides whether to persist (after user approval).
export function resolveProposalPlaceholders(
  contentJson: any,
  resolve: (inner: string) => string | null,
): PlaceholderScan {
  const filled: PlaceholderFill[] = [];
  const unresolved: PlaceholderGap[] = [];
  const next = { ...(contentJson || {}) };

  if (typeof next.coverLetter === 'string') {
    next.coverLetter = replaceInSection(next.coverLetter, resolve, 'Cover Letter', filled, unresolved);
  }
  if (Array.isArray(next.technicalSections)) {
    next.technicalSections = next.technicalSections.map((s: any) => ({
      ...s,
      content: typeof s?.content === 'string'
        ? replaceInSection(s.content, resolve, s.title || 'Section', filled, unresolved)
        : s?.content,
    }));
  }

  const affectedSections = Array.from(new Set(filled.map((f) => f.section)));
  return { updatedContentJson: next, filled, unresolved, affectedSections };
}

// Quick check: which sections still contain ANY bracket placeholder (excluding
// citation markers). Used to prune resolved "placeholder" gaps after a fill.
export function sectionsWithPlaceholders(contentJson: any): Set<string> {
  const out = new Set<string>();
  const scan = (content: string, section: string) => {
    if (!content) return;
    let text = content;
    const re = /\[([^\]\n]{1,80})\]/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (!/^[A-Z]{2,5}-\d+$/.test(m[1].trim())) { out.add(section); break; }
    }
  };
  if (typeof contentJson?.coverLetter === 'string') scan(contentJson.coverLetter, 'Cover Letter');
  for (const s of (contentJson?.technicalSections || [])) {
    if (typeof s?.content === 'string') scan(s.content, s.title || 'Section');
  }
  return out;
}
